import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useWallet } from '../contexts/WalletContext';
import { useTokens } from '../hooks/useTokens';
import { formatAmount, amountToCents, centsToAmount } from '../utils/hathor';
// TODO: Re-enable when snap supports timelock
// import { dateToUnixTimestamp, isFutureDate, getTimezoneOffset } from '../utils/timelock';
import { Address, Network, TokenVersion } from '@hathor/wallet-lib';
import { TOKEN_IDS } from '../constants';
import { readOnlyWalletWrapper } from '../services/ReadOnlyWalletWrapper';
import { getAddressForMode } from '../utils/addressMode';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { extractErrorMessage, getSnapErrorUserMessage } from '../utils/snapErrors';

interface SendDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialTokenUid?: string;
}

// Create a Zod schema factory for form validation
// Note: Balance validation is done separately in useEffect to handle dynamic token changes
const createSendFormSchema = (network: string) =>
  z.object({
    selectedToken: z.string(),
    amount: z
      .string()
      .min(1, 'Amount is required')
      .refine((val) => {
        // Basic format validation - allows both whole numbers and decimals
        return /^\d+(\.\d{1,2})?$/.test(val);
      }, 'Invalid amount format. Use up to 2 decimal places.')
      .refine((val) => {
        try {
          const amountInCents = amountToCents(val);
          return amountInCents > 0n;
        } catch {
          return false;
        }
      }, 'Amount must be greater than 0'),
    address: z
      .string()
      .min(1, 'Address is required')
      .superRefine((val, ctx) => {
        try {
          const networkObj = new Network(network);
          const addressObj = new Address(val.trim(), { network: networkObj });
          addressObj.validateAddress();

          // Disallow P2SH addresses
          if (addressObj.getType() === 'p2sh') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Sending to P2SH addresses is not supported',
            });
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : 'Invalid address';
          let customMessage = 'Invalid Hathor address format';

          if (errorMsg.includes('checksum')) {
            customMessage = 'Invalid address checksum. Please check the address.';
          } else if (errorMsg.includes('network')) {
            customMessage = `Invalid address for ${network} network`;
          }

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: customMessage,
          });
        }
      }),
    // TODO: Re-enable when snap supports timelock
    // timelock: z.date().optional().refine(
    //   (date) => !date || isFutureDate(date),
    //   'Timelock date must be in the future'
    // ),
    dataOutput: z.string().optional(),
  });

type SendFormData = z.infer<ReturnType<typeof createSendFormSchema>>;

const SendDialog: React.FC<SendDialogProps> = ({ isOpen, onClose, initialTokenUid }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [transactionError, setTransactionError] = useState<string | null>(null);

  const { sendTransaction, network, addressMode } = useWallet();
  const { allTokens } = useTokens();

  // Initialize form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
    reset,
  } = useForm<SendFormData>({
    resolver: zodResolver(createSendFormSchema(network)),
    defaultValues: {
      selectedToken: initialTokenUid || TOKEN_IDS.HTR,
      amount: '',
      address: '',
      // TODO: Re-enable when snap supports timelock
      // timelock: undefined,
      dataOutput: '',
    },
    mode: 'onChange',
  });

  // Reset form with selected token when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      reset({
        selectedToken: initialTokenUid || TOKEN_IDS.HTR,
        amount: '',
        address: '',
        // TODO: Re-enable when snap supports timelock
        // timelock: undefined,
        dataOutput: '',
      });
    }
  }, [isOpen, initialTokenUid, reset]);

  const amount = watch('amount');
  const address = watch('address');
  const selectedTokenUid = watch('selectedToken');

  // Get the selected token's balance
  const selectedToken = React.useMemo(() => {
    return allTokens.find(t => t.uid === selectedTokenUid);
  }, [allTokens, selectedTokenUid]);

  const availableBalance = selectedToken?.balance?.available || 0n;

  // When switching to an NFT token, strip any decimal portion from the amount
  React.useEffect(() => {
    if (selectedToken?.isNFT) {
      const currentAmount = getValues('amount');
      if (currentAmount.includes('.') || currentAmount.includes(',')) {
        setValue('amount', currentAmount.split(/[.,]/)[0]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTokenUid]);

  // Separate balance error state to avoid fighting with Zod's form errors
  const [balanceError, setBalanceError] = React.useState<string | null>(null);

  // Validate amount: NFT format and balance check
  // This runs separately from Zod schema to handle dynamic token changes
  React.useEffect(() => {
    if (!amount) {
      setBalanceError(null);
      return;
    }

    // First check NFT format
    if (selectedToken?.isNFT && amount.includes('.')) {
      setBalanceError('NFT amounts must be whole numbers only.');
      return;
    }

    // Then check balance
    try {
      const amountInBaseUnits = selectedToken?.isNFT
        ? BigInt(amount)
        : amountToCents(amount);

      if (amountInBaseUnits > availableBalance) {
        setBalanceError('Insufficient balance');
        return;
      }
    } catch {
      // amountToCents will throw for invalid formats, let Zod handle that
      setBalanceError(null);
      return;
    }

    setBalanceError(null);
  }, [amount, selectedToken?.isNFT, availableBalance]);

  const handleMaxClick = () => {
    if (availableBalance > 0n) {
      // NFTs are already in whole units, no conversion needed
      // Regular tokens need to be converted from base units (cents) to display units
      const maxAmount = selectedToken?.isNFT
        ? availableBalance.toString()
        : centsToAmount(availableBalance);
      setValue('amount', maxAmount, {
        shouldValidate: true
      });
    }
  };

  const onSubmit = async (data: SendFormData) => {
    setIsLoading(true);
    setTransactionError(null);

    try {
      // For NFTs, amount is already in base units (whole numbers)
      // For regular tokens, convert from display units to base units (cents)
      const amountInBaseUnits = selectedToken?.isNFT
        ? BigInt(data.amount)
        : amountToCents(data.amount);

      // Final balance check to ensure we have sufficient funds
      // This prevents race conditions where balance changed after form validation
      if (amountInBaseUnits > availableBalance) {
        const shortfall = amountInBaseUnits - availableBalance;
        const displayAmount = selectedToken?.isNFT
          ? amountInBaseUnits.toString()
          : centsToAmount(amountInBaseUnits);
        const displayAvailable = selectedToken?.isNFT
          ? availableBalance.toString()
          : centsToAmount(availableBalance);
        const displayShortfall = selectedToken?.isNFT
          ? shortfall.toString()
          : centsToAmount(shortfall);

        setTransactionError(
          `Insufficient balance. You need ${displayAmount} ${selectedToken?.symbol} ` +
          `but only have ${displayAvailable} ${selectedToken?.symbol} available. ` +
          `Short by ${displayShortfall} ${selectedToken?.symbol}.`
        );
        setIsLoading(false);
        return;
      }

      const changeAddress = await getAddressForMode(addressMode, readOnlyWalletWrapper);

      // TODO: Re-enable timelock when snap supports it
      const output: {
        address: string;
        value: string;
        token: string;
        // timelock?: number;
      } = {
        address: data.address.trim(),
        value: amountInBaseUnits.toString(),
        token: data.selectedToken
      };

      // TODO: Re-enable when snap supports timelock
      // Add timelock if provided (convert Date to Unix timestamp)
      // if (data.timelock) {
      //   output.timelock = dateToUnixTimestamp(data.timelock);
      //   console.log('[SendDialog] Timelock added:', {
      //     date: data.timelock,
      //     timestamp: output.timelock,
      //     formatted: new Date(output.timelock * 1000).toLocaleString()
      //   });
      // } else {
      //   console.log('[SendDialog] No timelock specified');
      // }

      const outputs: Array<typeof output | { type: string; data: string }> = [output];

      if (data.dataOutput?.trim()) {
        outputs.push({
          type: 'data',
          data: data.dataOutput.trim()
        });
      }

      await sendTransaction({
        network,
        outputs,
        changeAddress,
        push_tx: true,
      });

      // Note: Balance refresh is handled by WebSocket 'new-tx' event handler,
      // which refreshes only the affected tokens. No manual refresh needed here.
      reset();
      setTransactionError(null);
      onClose();
    } catch (err) {
      const rawMessage = extractErrorMessage(err, 'Failed to send transaction');
      // We only distinguish NATIVE here; for custom tokens we don't track the
      // DEPOSIT/FEE flag locally, and the default (network-fee) message is the
      // best fit for the most common case (fee-token send running out of HTR).
      const tokenVersion = data.selectedToken === TOKEN_IDS.HTR
        ? TokenVersion.NATIVE
        : undefined;
      setTransactionError(getSnapErrorUserMessage(rawMessage, { tokenVersion }));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-start md:items-center justify-center z-50 overflow-y-auto p-4 md:p-0"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#191C21] border border-[#24292F] rounded-2xl w-full max-w-md my-4 md:my-0 md:mx-4">
        {/* Header */}
        <div className="sticky top-0 bg-[#191C21] z-10 rounded-t-2xl relative flex items-center justify-center p-6">
          <h2 className="text-base font-bold text-primary-400">Send Tokens</h2>
          <button
            onClick={onClose}
            className="absolute right-6 p-1 hover:bg-secondary/20 rounded transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <fieldset disabled={isLoading} className="space-y-6 disabled:opacity-60">
          {/* Select Token */}
          <div>
            <label className="block text-base font-bold text-white mb-2">
              Select Token
            </label>
            <Select
              key={isOpen ? selectedTokenUid : 'closed'}
              value={selectedTokenUid}
              onValueChange={(value) => setValue('selectedToken', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a token..." />
              </SelectTrigger>
              <SelectContent>
                {allTokens.map((token) => (
                  <SelectItem key={token.uid} value={token.uid}>
                    {token.symbol} - {token.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-base font-bold text-white mb-2">
              Amount
            </label>
            <div className="relative">
              <input
                type="text"
                {...register('amount')}
                placeholder={selectedToken?.isNFT ? '0' : '0.0'}
                inputMode={selectedToken?.isNFT ? 'numeric' : 'decimal'}
                pattern={selectedToken?.isNFT ? '[0-9]*' : undefined}
                onKeyDown={(e) => {
                  if (e.key === ',' || (selectedToken?.isNFT && e.key === '.')) {
                    e.preventDefault();
                  }
                }}
                className={`w-full px-3 py-2 pr-12 bg-[#0D1117] border ${
                  errors.amount || balanceError ? 'border-red-500' : 'border-border'
                } rounded-lg text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary`}
              />
              <span className="absolute right-3 top-2.5 text-sm text-muted-foreground">
                {selectedToken?.symbol || 'HTR'}
              </span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-muted-foreground uppercase">
                Balance available: {formatAmount(availableBalance, selectedToken?.isNFT || false)} {selectedToken?.symbol || 'HTR'}
              </span>
              <button
                type="button"
                onClick={handleMaxClick}
                className="text-xs text-primary hover:text-primary/80 transition-colors uppercase"
              >
                Max
              </button>
            </div>
            {(errors.amount || balanceError) && (
              <div className="flex items-center gap-1 mt-1">
                <span className="text-xs text-red-400">{balanceError || errors.amount?.message}</span>
              </div>
            )}
          </div>

          {/* Destination Address */}
          <div>
            <label className="block text-base font-bold text-white mb-2">
              Destination Address
            </label>
            <input
              type="text"
              {...register('address')}
              placeholder="Address"
              className={`w-full px-3 py-2 bg-[#0D1117] border ${
                errors.address ? 'border-red-500' : 'border-border'
              } rounded-lg text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary`}
            />
            {errors.address && (
              <div className="flex items-center gap-1 mt-1">
                <span className="text-xs text-red-400">{errors.address.message}</span>
              </div>
            )}
          </div>

          {/* Advanced Options */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-base font-bold text-white hover:text-primary transition-colors"
            >
              Advanced options
              {showAdvanced ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-4">
                {/* TODO: Re-enable when snap supports timelock */}
                {/* Timelock */}
                {/* <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Timelock (optional)
                    <span className="ml-2 text-xs text-muted-foreground">
                      {getTimezoneOffset()}
                    </span>
                  </label>
                  <input
                    type="datetime-local"
                    onChange={(e) => {
                      const dateValue = e.target.value;
                      if (dateValue) {
                        const date = new Date(dateValue);
                        setValue('timelock', date, { shouldValidate: true });
                      } else {
                        setValue('timelock', undefined, { shouldValidate: true });
                      }
                    }}
                    min={(() => {
                      // Convert current time to local timezone for datetime-local input
                      const now = new Date();
                      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
                      return localDateTime.toISOString().slice(0, 16);
                    })()}
                    className="w-full px-3 py-2 bg-[#0D1117] border border-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {errors.timelock && (
                    <p className="mt-1 text-sm text-red-400">{errors.timelock.message}</p>
                  )}
                </div> */}

                {/* Data Output */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Data output (optional)
                  </label>
                  <textarea
                    {...register('dataOutput')}
                    placeholder="Optional message or metadata"
                    rows={3}
                    className="w-full px-3 py-2 bg-[#0D1117] border border-border rounded-lg text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  />
                </div>
              </div>
            )}
          </div>

          {transactionError && (
            <div className="flex flex-col gap-2 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
              <span className="text-red-400 text-sm whitespace-pre-line">{transactionError}</span>
              {transactionError.includes('permission') && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    window.location.reload();
                  }}
                  className="text-xs text-primary hover:text-primary/80 underline self-start"
                >
                  Click here to refresh and reconnect
                </button>
              )}
            </div>
          )}

          </fieldset>

          {/* Send Button - outside fieldset so it's not affected by disabled opacity */}
          <button
            type="submit"
            disabled={isLoading || !amount || !address || !!balanceError}
            className="w-auto mx-auto px-8 py-2.5 bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending transaction
              </>
            ) : (
              'Send token'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SendDialog;
